import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useAgents } from "../../../admin/agents/useAgents";
import type { ClientFilters } from "../clientFilters";
import { buildActiveFilterChips } from "./activeFilterChipsHelper";
import { filterChipSx } from "./filterChipSx";

interface Props {
  filters: ClientFilters;
  resultCount: number;
  onRemove: (key: keyof ClientFilters, value: string) => void;
  onReset: () => void;
}

/** Always rendered so the result count stays visible; the reset button appears only when a filter is set. */
const ActiveFilterChips: FC<Props> = ({ filters, resultCount, onRemove, onReset }) => {
  const { agents } = useAgents();
  const chips = useMemo(
    () => buildActiveFilterChips(filters, (agentId) => agents.find((agent) => agent.id === agentId)?.name ?? agentId),
    [filters, agents],
  );

  return (
    <Box
      role="region"
      aria-label="Aktywne filtry"
      sx={{
        mb: 2,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        bgcolor: "rgba(0,0,0,0.04)",
        border: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
        Aktywne filtry:
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", flex: 1, minWidth: 0 }}>
        {!chips.length && (
          <Typography variant="body2" color="text.disabled">
            brak
          </Typography>
        )}
        {chips.map((chip) => (
          <Tooltip key={`${chip.key}-${chip.value}`} title={chip.tooltip ?? ""} describeChild>
            <Chip label={chip.label} size="small" onDelete={() => onRemove(chip.key, chip.value)} sx={filterChipSx} />
          </Tooltip>
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
        Wyniki: {resultCount}
      </Typography>
      {chips.length > 0 && (
        <Button
          size="small"
          color="inherit"
          onClick={onReset}
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontWeight: 600,
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
          }}
        >
          Wyczyść
        </Button>
      )}
    </Box>
  );
};

export default ActiveFilterChips;
